import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CYB } from '@wallets/sdk';

import Button from 'components/common/Button';
import AlertBadge from 'components/common/AlertBadge';
import styles from './CybersourceInstallmentsForm.module.scss';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import { setCardByToken } from 'store/card/cybersource.slice';
import { RootState } from 'types/store';

export type CybInstallmentsFormProps = React.HTMLAttributes<HTMLFormElement> & {
    card: CardToken;
};

export const CybersourceInstallmentsForm = (props: CybInstallmentsFormProps) => {
    const { t } = useTranslation('error');
    const dispatch = useAppDispatch();
    const pm = useAppSelector((state: RootState) => state.paymentMethod);
    const user = useAppSelector((state: RootState) => state.user);
    const shippingInfo = useAppSelector((state: RootState) => state.cart).data?.shippingInfo;
    const cyb = pm.types.cybersource.data;
    const token = useAppSelector((state: RootState) => state.auth).data?.accessToken;
    // eslint-disable-next-line
    const onSubmit = useRef<null | any>(null);
    const [error, setError] = useState<null | string>(null);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        (async () => {
            if (cyb && user.data && token && shippingInfo) {
                try {
                    const micro = await CYB.initializeMicroformV2(cyb.jwk, {}, false);

                    const userForPayment = {
                        firstName: user.data.firstname,
                        lastName: user.data.lastname,
                        email: user.data.email,
                        phoneNumber: shippingInfo[0].customer.phoneNumber,
                    };

                    onSubmit.current = async () =>
                        await CYB.tokenizeCardWithMicroformV2(
                            micro,
                            userForPayment,
                            cyb.id,
                            false,
                            token,
                        );
                } catch (err) {
                    setError(t('cybDefaultError.message'));
                }
            }
        })();
        // eslint-disable-next-line
    }, []);

    const handleOnSubmit = async (event: FormEvent) => {
        event.stopPropagation();
        setError(null);

        if (onSubmit.current && cyb && props.card) {
            setIsSending(true);
            const resp = await onSubmit.current();

            if (resp.data && resp.data.success) {
                setIsSending(false);
                await dispatch(setCardByToken({ card: props.card, payment: cyb })).unwrap();
            } else if (resp.error.detail == 'Validation error') {
                setError(t('cybValidationError.message'));
            } else {
                setError(t('cybCvvError.message'));
            }
            setIsSending(false);
        }
    };

    return (
        <form
            className={`${styles['container']}`}
            id="phub-cybv3-form"
            data-testid="phub-cybv3-form"
            onSubmit={handleOnSubmit}
        >
            <div className={styles['row']}>
                <div className={styles['field']}>
                    <div
                        id="phub-cybv3-security-container"
                        className={styles['flex']}
                        data-testid="cybv3-security-container"
                    />
                </div>
                <div
                    className={styles['button-container']}
                    data-testid="cybv3-pay-button-container"
                >
                    <Button
                        id="phub-cybv3-pay-button"
                        theme="primary"
                        size="large"
                        className={styles['button']}
                        loading={isSending}
                        disabled={isSending}
                        onClick={handleOnSubmit}
                    >
                        Verificar
                    </Button>
                </div>
            </div>

            {error && (
                <div className={styles['error']}>
                    <AlertBadge theme="danger" size="medium">
                        {error}
                    </AlertBadge>
                </div>
            )}
        </form>
    );
};
